/**
 * Shared schemas for the HubSpot-enriched returns analytics handlers.
 * Consumers: activation funnel, cohort retention, pipeline funnel,
 * churn breakdown, etc.
 */
import * as z from "zod";

/**
 * Lightweight date range input. Defaults to last 90 days when start/end
 * are omitted. URL-based — ?startDate=...&endDate=... in admin app.
 */
export const returnsAnalyticsDateRangeSchema = z.object({
  startDate: z.string().min(1).optional(),
  endDate: z.string().min(1).optional(),
});
export type ReturnsAnalyticsDateRange = z.infer<
  typeof returnsAnalyticsDateRangeSchema
>;

/**
 * Data quality snapshot returned by every analytics handler so the UI
 * can show warning banners when data is stale or sparse.
 */
export const returnsAnalyticsDataQualitySchema = z.object({
  hubspotSyncLastRunAt: z.date().nullable(),
  lifecycleRecomputeLastRunAt: z.date().nullable(),
  closedWonSampleSize: z.number().int().min(0),
  totalReturnsDeals: z.number().int().min(0),
});
export type ReturnsAnalyticsDataQuality = z.infer<
  typeof returnsAnalyticsDataQualitySchema
>;

/**
 * Revenue columns on hubspot_deals. Returned by handlers that aggregate
 * revenue so the UI can toggle between amount / ARR / MRR / preferred.
 * Values stored as strings because they're bigint-sized in CRDB.
 */
export const returnsRevenueBundleSchema = z.object({
  amountCents: z.string(),
  arrCents: z.string(),
  mrrCents: z.string(),
  preferredCents: z.string(),
});
export type ReturnsRevenueBundle = z.infer<typeof returnsRevenueBundleSchema>;
